import React, { useMemo, useState, useCallback } from 'react';
import { Box, Paper, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Typography, styled, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import { generateOccurrences } from '../../utils/generateOccurrences';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const StickyHead = styled(TableCell)(({ theme }) => ({
  position: 'sticky',
  left: 0,
  top: 0, // At the top of the scrollable container
  background: theme.palette.background.paper,
  zIndex: 100, // highest z-index to stay above everything
  fontWeight: 600,
  minWidth: 210, // Increased to match the cell width
  // On phones the 200px name column ate over half the viewport
  [theme.breakpoints.down('sm')]: { minWidth: 150, maxWidth: 170 },
  boxShadow: '2px 0 4px -2px rgba(0,0,0,0.12)',
  // Prevent content bleeding from left
  '&::before': {
    content: '""',
    position: 'absolute',
    left: -20,
    top: 0,
    bottom: 0,
    width: 20,
    background: theme.palette.background.paper,
    zIndex: -1
  },
  // Override any MUI z-index
  '&.MuiTableCell-stickyHeader': {
    zIndex: '100 !important'
  }
}));



const StickyCell = styled(TableCell)(({ theme }) => ({ 
  position: 'sticky', 
  left: 0, 
  background: theme.palette.background.paper, 
  zIndex: 30, 
  fontWeight: 500, 
  whiteSpace: 'nowrap',
  minWidth: 210, // Increased to accommodate action buttons
  // On phones the 200px name column ate over half the viewport
  [theme.breakpoints.down('sm')]: { minWidth: 150, maxWidth: 170 },
  // Prevent content bleeding from left
  '&::before': {
    content: '""',
    position: 'absolute',
    left: -20,
    top: 0,
    bottom: 0,
    width: 20,
    background: theme.palette.background.paper,
    zIndex: -1
  }
}));



// Amount an action contributes to a given month: its per-occurrence amount
// times the exact number of occurrences the shared projection puts in that
// month (a weekly action shows 4 or 5 weeks, not a 4.33 approximation).
function monthlyAmount(action, year, monthIndex) {
  const monthStart = new Date(year, monthIndex, 1);
  const monthEnd = new Date(year, monthIndex + 1, 0);
  const count = generateOccurrences(action, monthStart, monthEnd).length;
  return count * (Number(action.amount) || 0);
}

const formatCurrency = (v) => v ? new Intl.NumberFormat('en-US',{style:'currency',currency:'USD',maximumFractionDigits:0}).format(v) : '—';

const ActionMatrixView = ({ actions, accounts, year: externalYear, onYearChange, onEditAction, onDeleteAction }) => {
  const [year,setYear] = useState(externalYear || new Date().getFullYear());
  const [expandedSections, setExpandedSections] = useState(new Set());
  
  React.useEffect(()=>{ if(externalYear && externalYear!==year) setYear(externalYear); },[externalYear,year]);
  const changeYear = (d)=>{ const ny=year+d; setYear(ny); onYearChange && onYearChange(ny); };

  // Toggle section expansion
  const toggleSection = useCallback((sectionKey) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionKey)) {
        newSet.delete(sectionKey);
      } else {
        newSet.add(sectionKey);
      }
      return newSet;
    });
  }, []);

  // Build hierarchical rows per account with child action rows
  const accountRows = useMemo(()=>{
    const rows = accounts.filter(a=>a.isActive).map(acc=>{
      // Actions impacting account: source (negative), destination (positive)
      // Filter out completed actions so they disappear from the matrix
      const impacting = actions.filter(act=> act.isActive && !act.isCompleted && (act.sourceAccountId===acc.id || act.destinationAccountId===acc.id));
      // Helper to determine impact sign for this account per action rules
      const impactSign = (action, accountId) => {
        switch(action.type){
          case 'transfer':
            if(action.sourceAccountId === accountId) return -1; // funds leave
            if(action.destinationAccountId === accountId) return 1; // funds arrive
            return 0;
          case 'income':
            // Income with only a source account increases that account (source represents receiving account in this model)
            if(action.sourceAccountId === accountId) return 1;
            // If a destination is ever set for income, treat destination as receiver as well
            if(action.destinationAccountId && action.destinationAccountId === accountId) return 1;
            return 0;
          case 'expense':
          default:
            if(action.sourceAccountId === accountId) return -1; // expense reduces the source account
            return 0;
        }
      };

      const actionRows = impacting.map(act=>{
        const monthly = MONTHS.map((_,i)=>{
          const amt = monthlyAmount(act, year, i);
          if(!amt) return 0;
          const sign = impactSign(act, acc.id);
          if(sign === 0) return 0;
          return amt * sign;
        });
        const total = monthly.reduce((s,v)=>s+v,0);
        return { act, monthly, total };
      }).filter(r=> r.monthly.some(v=> v!==0));
      const monthlyTotals = MONTHS.map((_,i)=> actionRows.reduce((s,r)=> s + r.monthly[i],0));
      const total = monthlyTotals.reduce((s,v)=> s+v,0);
      return { account: acc, monthly: monthlyTotals, total, actions: actionRows };
    }).filter(r=> r.actions.length>0);
    
    return rows;
  },[accounts, actions, year]);

  // Compute totals across all accounts similar to budgets (income/expense/net)
  const totalsRow = useMemo(() => {
    const incomeTotals = MONTHS.map((_, i) => accountRows.reduce((s, r) => s + (r.monthly[i] > 0 ? r.monthly[i] : 0), 0));
    const expenseTotals = MONTHS.map((_, i) => accountRows.reduce((s, r) => s + (r.monthly[i] < 0 ? Math.abs(r.monthly[i]) : 0), 0));
    const netTotals = MONTHS.map((_, i) => incomeTotals[i] - expenseTotals[i]);
    const incomeGrand = incomeTotals.reduce((s, v) => s + v, 0);
    const expenseGrand = expenseTotals.reduce((s, v) => s + v, 0);
    const netGrand = netTotals.reduce((s, v) => s + v, 0);
    return { incomeTotals, expenseTotals, netTotals, incomeGrand, expenseGrand, netGrand };
  }, [accountRows]);

  // Expand/collapse all sections
  const expandAll = useCallback(() => {
    const allSectionKeys = accountRows.map(row => row.account.id);
    setExpandedSections(new Set(allSectionKeys));
  }, [accountRows]);

  const collapseAll = useCallback(() => {
    setExpandedSections(new Set());
  }, []);

  // Auto-expand accounts with fewer actions for better initial UX
  React.useEffect(() => {
    const autoExpandKeys = accountRows
      .filter(row => row.actions.length <= 3) // Auto-expand accounts with 3 or fewer actions
      .map(row => row.account.id);
    
    setExpandedSections(new Set(autoExpandKeys));
  }, [accountRows]);

  // Totals removed per requirement

  return (
    <Paper sx={{ borderRadius: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ 
        display:'flex', 
        justifyContent:'center', 
        alignItems:'center', 
        p: 1.5,
        pb: 1,
        background: (theme) => theme.palette.background.paper,
        borderBottom: '1px solid',
        borderColor: 'divider',
        flexShrink: 0 // Prevent shrinking
      }}>
        {/* Center: Year navigator */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25 }}>
          <IconButton
            size="small"
            onClick={() => changeYear(-1)}
            sx={{ p: 0.6, bgcolor: 'common.white', boxShadow: '0 2px 4px rgba(0,0,0,0.04)', borderRadius: 3, width: 40, height: 40, border: '1px solid', borderColor: 'grey.300', '&:hover': { bgcolor: 'grey.50' } }}
          >
            <KeyboardArrowLeftIcon sx={{ fontSize: '0.875rem', color: 'text.primary' }} />
          </IconButton>

          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'text.secondary', textAlign: 'center', minWidth: 92, letterSpacing: 0.6, textTransform: 'uppercase' }}>
            {year}
          </Typography>

          <IconButton
            size="small"
            onClick={() => changeYear(1)}
            sx={{ p: 0.6, bgcolor: 'common.white', boxShadow: '0 2px 4px rgba(0,0,0,0.04)', borderRadius: 3, width: 40, height: 40, border: '1px solid', borderColor: 'grey.300', '&:hover': { bgcolor: 'grey.50' } }}
          >
            <KeyboardArrowRightIcon sx={{ fontSize: '0.875rem', color: 'text.primary' }} />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', p: 1.5, pt: 0 }}>
        {/* Phones: natural table width so the narrowed sticky column doesn't
            leave a stretched empty gutter; desktop keeps the 1160 floor. */}
        <Box sx={{ minWidth: { xs: 0, sm: 1200 }, width: { xs: 'max-content', sm: 'auto' } }}>
        <Table size="small" stickyHeader sx={{
          '& th, & td': { borderRight:'1px solid', borderColor:'divider' },
          '& th:last-of-type, & td:last-of-type': { borderRight:'none' },
          '& thead th': {
            position: 'sticky',
            top: 0, // At the top of the scrollable container
            background: (theme)=>theme.palette.background.paper,
            zIndex: 5
          }
        }}>
          <TableHead>
            <TableRow>
              <StickyHead sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation();
                    const allExpanded = accountRows.length > 0 && accountRows.every(r => expandedSections.has(r.account.id));
                    if (allExpanded) collapseAll(); else expandAll();
                  }}
                  aria-label={"Toggle expand all accounts"}
                  sx={{
                    p: 0.25,
                    bgcolor: 'rgba(0,0,0,0.06)',
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                    transform: accountRows.length > 0 && accountRows.every(r => expandedSections.has(r.account.id)) ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: 'transform 0.2s',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' }
                  }}
                >
                  <ExpandMoreIcon fontSize="small" />
                </IconButton>
                Account
              </StickyHead>
              {MONTHS.map(m=> <TableCell key={m} align="center" sx={{ fontSize:'0.8125rem', fontWeight:600 }}>{m}</TableCell>)}
              <TableCell align="right" sx={{ fontSize:'0.8125rem', fontWeight:600 }}>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accountRows.map(row=> {
              const sectionKey = row.account.id;
              const isExpanded = expandedSections.has(sectionKey);
              
              return (
                <React.Fragment key={row.account.id}>
                  <TableRow 
                      hover 
                      onClick={() => toggleSection(sectionKey)}
                    >
                      <StickyCell sx={{ fontWeight:700, display: 'flex', alignItems: 'center', gap: 1, backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06) }}>
                      <IconButton 
                        size="small" 
                        sx={{ 
                          p: 0.25,
                          transition: 'transform 0.2s',
                          transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'
                        }}
                      >
                        <ExpandMoreIcon fontSize="small" />
                      </IconButton>
                      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <Box component="span" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.account.name}>{row.account.name}</Box>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1 }}>
                          {row.actions.length} action{row.actions.length !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    </StickyCell>
                      {row.monthly.map((amt,i)=> <TableCell key={i} align="right" sx={{ fontSize:'0.75rem', fontWeight:700, color: amt>=0 ? 'success.main':'error.main', background: (theme) => amt>=0 ? alpha(theme.palette.success.main, 0.03) : alpha(theme.palette.error.main, 0.03) }}>{formatCurrency(Math.abs(amt))}</TableCell>)}
                      <TableCell align="right" sx={{ fontSize:'0.75rem', fontWeight:700, color: row.total>=0 ? 'success.main':'error.main', background: (theme) => row.total>=0 ? alpha(theme.palette.success.main, 0.04) : alpha(theme.palette.error.main, 0.04) }}>{formatCurrency(Math.abs(row.total))}</TableCell>
                  </TableRow>
                  {isExpanded && row.actions.map(actionRow=> (
                    <TableRow key={actionRow.act.id}>
                      <StickyCell sx={{ pl:3, fontSize:'0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{actionRow.act.name}</span>
                        <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                          <Tooltip title="Edit Action">
                            <IconButton 
                              size="small" 
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditAction && onEditAction(actionRow.act);
                              }}
                              sx={{ 
                                p: 0.25,
                                '&:hover': {
                                  backgroundColor: 'primary.light',
                                  color: 'primary.contrastText'
                                }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {/* Completion removed */}
                          {onDeleteAction && (
                            <Tooltip title="Delete Action">
                              <IconButton 
                                size="small" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDeleteAction && onDeleteAction(actionRow.act);
                                }}
                                sx={{ 
                                  p: 0.25,
                                  color: 'error.main',
                                  '&:hover': {
                                    backgroundColor: 'error.light',
                                    color: 'error.contrastText'
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </StickyCell>
                      {actionRow.monthly.map((amt,i)=> <TableCell key={i} align="right" sx={{ fontSize:'0.8125rem', fontWeight:600, color: amt>=0 ? 'success.main':'error.main' }}>{amt ? formatCurrency(Math.abs(amt)) : '—'}</TableCell>)}
                      <TableCell align="right" sx={{ fontSize:'0.8125rem', fontWeight:700, color: actionRow.total>=0 ? 'success.main':'error.main' }}>{actionRow.total ? formatCurrency(Math.abs(actionRow.total)) : '—'}</TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              );
            })}
            {accountRows.length > 0 && (
              <>
                {/* Income Totals */}
                <TableRow>
                  <StickyCell sx={{ fontWeight: 600, background: (theme) => alpha(theme.palette.success.main, 0.06) }}>Income Total</StickyCell>
                  {totalsRow.incomeTotals.map((amt, idx) => (
                    <TableCell key={idx} align="right" sx={{ fontSize:'0.75rem', fontWeight:700, color: 'success.main', background: (theme) => alpha(theme.palette.success.main, 0.04) }}>
                      {formatCurrency(amt)}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontSize:'0.75rem', fontWeight:700, color: 'success.main', background: (theme) => alpha(theme.palette.success.main, 0.04) }}>{formatCurrency(totalsRow.incomeGrand)}</TableCell>
                </TableRow>
                {/* Expense Totals */}
                <TableRow>
                  <StickyCell sx={{ fontWeight: 600, background: (theme) => alpha(theme.palette.error.main, 0.06) }}>Expense Total</StickyCell>
                  {totalsRow.expenseTotals.map((amt, idx) => (
                    <TableCell key={idx} align="right" sx={{ fontSize:'0.75rem', fontWeight:700, color: 'error.main', background: (theme) => alpha(theme.palette.error.main, 0.04) }}>
                      {formatCurrency(amt)}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontSize:'0.75rem', fontWeight:700, color: 'error.main', background: (theme) => alpha(theme.palette.error.main, 0.04) }}>{formatCurrency(totalsRow.expenseGrand)}</TableCell>
                </TableRow>
                {/* Net Row */}
                <TableRow>
                  <StickyCell sx={{ fontWeight: 700, background: (theme) => alpha(theme.palette.text.primary, 0.04) }}>Net (Income - Expenses)</StickyCell>
                  {totalsRow.netTotals.map((amt, idx) => (
                    <TableCell key={idx} align="right" sx={{ fontSize:'0.75rem', fontWeight:700, color: amt >= 0 ? 'success.main' : 'error.main', background: (theme) => alpha(theme.palette.text.primary, 0.03) }}>
                      {formatCurrency(amt)}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ fontSize:'0.75rem', fontWeight:700, color: totalsRow.netGrand >= 0 ? 'success.main' : 'error.main', background: (theme) => alpha(theme.palette.text.primary, 0.03) }}>{formatCurrency(totalsRow.netGrand)}</TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
        </Box>
      </Box>
    </Paper>
  );
};

export default ActionMatrixView;
